import React, { useState, useEffect, useCallback } from 'react';
import MailDetailHeader from './MailDetailHeader';
import MailBody from './MailBody';
import MailThread from './MailThread';
import AttachmentChip from './AttachmentChip';
import InlineComposer from './InlineComposer';
import MailEmptyState from './MailEmptyState';
import SafeGuardReasonBanner from './SafeGuardReasonBanner';
import useThread from '../hooks/useThread';
import { printThreadExport } from '../utils/threadExport';

const OUTGOING_TABS = ['sent', 'replied', 'forwarded', 'scheduled'];
const COMPOSER_EXIT_MS = 340; // slightly longer than the .ivx-composer.closing animation (0.32s, reverse of open) so the held end-frame never pops

/**
 * MailDetail — the reading pane. Wires header + body + (optional) inline
 * composer together. The composer's open/close state is owned by the
 * orchestrator (InboxView) so it can guard all the ways the user might
 * accidentally discard unsent input.
 */
export default function MailDetail({
  userId,
  email,
  selectedTab,
  accounts,
  sending,
  generating,
  composerOpen,
  composerMode = 'reply',
  composerInitialText,
  composerSeed = 0,
  composerForwardTo = '',
  composerForwardScope = 'single',
  onOpenComposer,
  onRequestCloseComposer,
  onForceCloseComposer,
  onComposerDirtyChange,
  onSend,
  onGenerate,
  onDelete,
  deleteLabel,
  onSendNow,
  sendingNow,
  onResendAsIntended,
  onResendAll,
  resendLoading,
  onBlock,
  onAddToLeads,
  onForward,
  onClose,
  onNotify,
  deleting,
  blocking,
  isMobile,
}) {
  // Keep the composer mounted while it plays its exit animation. The parent
  // owns `composerOpen` (so all discard-guards stay intact); this only governs
  // the visual enter/leave so closing slides out instead of vanishing.
  const [composerVisible, setComposerVisible] = useState(composerOpen);
  const [composerClosing, setComposerClosing] = useState(false);

  // Full conversation for the selected mail (single message while it loads, or
  // for legacy mails without thread metadata).
  const { messages } = useThread(userId, email);
  const isThread = messages.length > 1;

  useEffect(() => {
    if (composerOpen) {
      setComposerVisible(true);
      setComposerClosing(false);
      return undefined;
    }
    if (!composerVisible) return undefined;
    setComposerClosing(true);
    const t = setTimeout(() => {
      setComposerVisible(false);
      setComposerClosing(false);
    }, COMPOSER_EXIT_MS);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composerOpen]);

  // Export lives here rather than in the orchestrator because the assembled
  // conversation only exists in this component (via useThread).
  const handleExportThread = useCallback(() => {
    const { ok, reason } = printThreadExport(messages, email);
    if (ok || !onNotify) return;
    onNotify(
      reason === 'blocked'
        ? 'Der Export wurde vom Popup-Blocker gestoppt — bitte Popups für diese Seite erlauben.'
        : 'Für diesen Verlauf gibt es keine Nachrichten zum Exportieren.',
      'error'
    );
  }, [messages, email, onNotify]);

  if (!email) return <MailEmptyState />;

  const isOutgoing = OUTGOING_TABS.includes(selectedTab);
  const canReply = !isOutgoing && selectedTab !== 'deleted' && selectedTab !== 'scheduled';
  // The composer hosts both reply and forward. Forwarding is allowed wherever
  // we can reply (incoming mail) and is opened explicitly via the menu.
  const composerAllowed = canReply || composerMode === 'forward';

  return (
    <div className="ivx-detail">
      <MailDetailHeader
        email={email}
        isOutgoing={isOutgoing}
        canReply={canReply}
        onReply={onOpenComposer}
        onForward={onForward}
        onDelete={onDelete}
        deleteLabel={deleteLabel}
        onSendNow={onSendNow}
        sendingNow={sendingNow}
        onResendAsIntended={onResendAsIntended}
        onResendAll={onResendAll}
        resendLoading={resendLoading}
        onBlock={onBlock}
        onAddToLeads={onAddToLeads}
        onExportThread={handleExportThread}
        exportLabel={isThread ? `Chatverlauf exportieren (${messages.length})` : 'Nachricht exportieren'}
        onClose={isMobile ? onClose : null}
        deleting={deleting}
        blocking={blocking}
        showMeta={!isThread}
      />

      <div className="ivx-detail-scroll">
        <SafeGuardReasonBanner email={email} />
        {isThread ? (
          <MailThread messages={messages} />
        ) : (
          <>
            <MailBody email={email} />

            {Array.isArray(email.attachments) && email.attachments.length > 0 && (
              <div className="ivx-detail-attachments">
                <span className="ivx-detail-attachments-label">
                  {email.attachments.length} attachment{email.attachments.length > 1 ? 's' : ''}
                </span>
                <div className="ivx-detail-attachments-list">
                  {email.attachments.map((a, i) => (
                    <AttachmentChip key={i} attachment={a} />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {composerAllowed && composerVisible && (
        <InlineComposer
          key={`${email.id}-${composerMode}-${composerSeed}`}
          userId={userId}
          originalEmail={email}
          accounts={accounts}
          sending={sending}
          generating={generating}
          closing={composerClosing}
          initialText={composerInitialText}
          initialForwardTo={composerForwardTo}
          initialForwardScope={composerForwardScope}
          mode={composerMode}
          threadMessages={messages}
          onSend={onSend}
          onGenerate={onGenerate}
          onRequestClose={onRequestCloseComposer}
          onForceClose={onForceCloseComposer}
          onDirtyChange={onComposerDirtyChange}
        />
      )}
    </div>
  );
}
